/**
 * レビューへの返信を書く欄（指摘ごとの聞き返しと、成果物ごとの会話）。
 *
 * 返信の器・API・MCP の口は入っていたのに、**画面は読む側だけだった**——経緯に
 * 「返信を記録」が並ぶのに、画面から返信を書く口が無い。結果として人は返信するために
 * Claude のセッションを開くことになり、「操作は画面と MCP の両方に置く」が片側だけ
 * 満たされた状態になっていた。ここがその書く側。
 *
 * **判定を写さない。** 書ける人（親・子チケットの担当者）・本文の上限・取り下げ済みの
 * レビュー・無い指摘 ID はすべて product-api と domain が弾き、画面は返ったメッセージを
 * そのまま出す。画面で止めるのは空の本文だけで、これは「押せるのに必ず弾かれるボタン」を
 * 消すためのもの（上限の数字は持たない）。
 *
 * **置き場所は 2 つ。** 未解決の指摘の隣（その指摘への聞き返し）と、経緯の末尾（どの指摘にも
 * 紐づかない成果物ごとの会話）。後者を指摘と同じように常時出すと、成果物 6 つぶんの空の入力欄が
 * 画面に居座って読むものを押しのけるので、**会話の末尾**——つまり読んだ流れの続き——に置く。
 *
 * **指摘の隣から送れたら経緯を開く。** 返信は経緯（`ReviewHistory`）の中に出来事として並ぶので、
 * 閉じたままだと書いた本人に何も見えない。文章で「記録しました」と言う代わりに、
 * 書いたものが見える場所を開く。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 要件定義のレビューの指摘へ画面から返信し、確認事項のやり取りを画面の中に残す
 * @graph-connects useAddSpecReviewComment [calls] 返信の記録
 * @graph-connects reviewErrorMessage [calls] 失敗した理由の文言
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAddSpecReviewComment } from '../../../hooks/useSpecRequirements.js';
import { reviewErrorMessage } from './review-error.js';

export interface ReviewReplyProps {
  /** 案件キー */
  pjKey: string;
  /** レビュー対象（業務フローは flow_id、案件に 1 つの成果物は成果物名） */
  targetId: string;
  /**
   * 返信を付ける指摘。**null は成果物ごとの会話**（どの指摘にも紐づかない確認事項）。
   *
   * API は `findingId` を省くと会話として扱うので、null のときは欄ごと送らない。
   */
  findingId: string | null;
  /** 対象の呼び名。同じ文言の欄が節ごと・指摘ごとに並ぶので、読み上げで区別するために使う */
  label: string;
  /**
   * 送れたときに呼ぶ。**経緯の中に置くときは要らない**（既に書いた返信が見える場所にいる）。
   * 指摘の隣に置くときは、ここで経緯を開いて書いたものが見える場所へ連れて行く。
   */
  onPosted?: () => void;
}

/**
 * 返信の欄 1 つ。
 *
 * 取得と同じく**欄ごとに `useMutation` を持つ**（`ReviewHistory` が出来事の取得を自分で
 * 持っているのと同じ）。1 つの mutation を上から配ると、どの指摘へ送っている最中かを
 * 鍵で引き当てる仕事が増え、隣の指摘の欄に処理中・失敗が漏れる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 指摘または成果物に対する返信を 1 件書いて記録する
 * @graph-connects useAddSpecReviewComment [calls] 返信の記録
 */
export function ReviewReply({ pjKey, targetId, findingId, label, onPosted }: ReviewReplyProps) {
  const { t } = useTranslation();
  const [body, setBody] = useState('');
  const addComment = useAddSpecReviewComment(pjKey);
  const trimmed = body.trim();

  return (
    <div className="mt-2">
      <textarea
        rows={2}
        className="block w-full border border-[var(--color-border)] rounded px-3 py-2 text-sm"
        placeholder={t('spec.review.replyPlaceholder')}
        aria-label={
          findingId === null
            ? t('spec.review.replyArtifactAria', { label })
            : t('spec.review.replyAria', { id: findingId, label })
        }
        value={body}
        onChange={(e) => {
          setBody(e.target.value);
        }}
      />
      <button
        type="button"
        disabled={addComment.isPending || trimmed === ''}
        className="mt-2 text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
        onClick={() => {
          addComment.mutate(
            {
              targetId,
              // `findingId: undefined` を送らない。API は欄の有無で会話と返信を分ける
              input: findingId === null ? { body: trimmed } : { body: trimmed, findingId },
            },
            {
              onSuccess: () => {
                setBody('');
                onPosted?.();
              },
            },
          );
        }}
      >
        {t('spec.review.reply')}
      </button>
      {addComment.error === null ? null : (
        <p role="alert" className="mt-2 text-xs text-[var(--color-error)] whitespace-pre-wrap">
          {reviewErrorMessage(t, addComment.error)}
        </p>
      )}
    </div>
  );
}
